/**
 * Per-hunk merge buttons for a Monaco DiffEditor.
 *
 * Monaco ships the diff — the alignment, the highlighting, the synced scroll —
 * but NOT the little arrows that copy one hunk across. Those are VS Code's own
 * UI built on top, so they have to be built here too.
 *
 * The whole feature reduces to two pure functions, kept apart from the editor
 * so they can be tested without a DOM: `hunkEdit` turns one of Monaco's line
 * changes into a text edit, and `applyHunkToText` applies it to a string. The
 * widget code that decorates the editor is the thin part.
 *
 * ## The off-by-one that makes this fiddly
 *
 * Monaco reports a pure INSERT (text present on the left, absent on the right)
 * as `modifiedStartLineNumber > modifiedEndLineNumber`, with `modifiedEndLine`
 * one BEHIND the start and both describing a gap rather than a range — the same
 * shape a pure DELETE takes on the original side. So a hunk cannot simply be
 * read as "replace lines start..end": for a gap you must insert AFTER
 * `modifiedStartLineNumber - 1` and replace nothing, and for a real range you
 * replace start..end inclusive. Treating the gap as a range deletes the line
 * above the insertion point — the classic bug in hand-rolled hunk-apply, and
 * the reason both shapes are covered in the tests.
 */

/** A hunk where one side has no lines — an insertion or a deletion, not a change. */
export function isGap(startLine, endLine) {
  return endLine < startLine;
}

/**
 * Turn one Monaco `LineChange` into `{ startLine, endLineExclusive, lines }`
 * describing what the MODIFIED (right/your) document should become.
 *
 * @param change  a Monaco line change
 * @param originalLines  the left document, split into lines
 */
export function hunkEdit(change, originalLines) {
  const {
    originalStartLineNumber: oStart,
    originalEndLineNumber: oEnd,
    modifiedStartLineNumber: mStart,
    modifiedEndLineNumber: mEnd,
  } = change;

  // Both sides use ONE formula, and the gap encoding falls out of it rather
  // than needing a branch — which is worth stating, because the branch looks
  // necessary and is not:
  //
  //   left  : slice(oStart - 1, oEnd)   — for a gap, oEnd = oStart - 1, so the
  //           slice is empty. A deletion contributes no lines. Correct.
  //   right : replace oStart..mEnd inclusive, i.e. endExclusive = mEnd + 1 —
  //           for a gap that is mStart, so the splice replaces NOTHING and
  //           inserts at that point. An insertion. Also correct.
  //
  // An earlier version special-cased both. The special cases computed the
  // identical values, so they were dead code that merely advertised danger.
  // `isGap` survives as the name for the encoding, used by the UI to label a
  // hunk and by the tests to assert this equivalence deliberately.
  const lines = originalLines.slice(oStart - 1, oEnd);
  return { startLine: mStart, endLineExclusive: mEnd + 1, lines };
}

/**
 * Apply one hunk to the modified text, returning the new text.
 *
 * Line-based rather than character-based on purpose: Monaco's line changes are
 * line-granular, and reconstructing character offsets from them is a second
 * chance to get the arithmetic wrong for no benefit.
 */
export function applyHunkToText(modifiedText, originalText, change) {
  const eol = detectEol(modifiedText) || detectEol(originalText) || '\n';
  const modifiedLines = splitLines(modifiedText);
  const originalLines = splitLines(originalText);

  const { startLine, endLineExclusive, lines } = hunkEdit(change, originalLines);
  const next = [
    ...modifiedLines.slice(0, startLine - 1),
    ...lines,
    ...modifiedLines.slice(endLineExclusive - 1),
  ];
  return next.join(eol);
}

/** How many lines this hunk will add to / remove from the right side. */
export function hunkSummary(change, originalLines) {
  const { startLine, endLineExclusive, lines } = hunkEdit(change, originalLines);
  const removed = Math.max(0, endLineExclusive - startLine);
  return { added: lines.length, removed };
}

export function splitLines(text) {
  return String(text ?? '').split(/\r\n|\r|\n/);
}

/** Preserve the file's existing line endings — rewriting a CRLF file with LF is
 *  a whole-file diff on the next comparison, and on some servers a broken one. */
export function detectEol(text) {
  if (typeof text !== 'string' || text.length === 0) return null;
  if (text.includes('\r\n')) return '\r\n';
  if (text.includes('\n')) return '\n';
  if (text.includes('\r')) return '\r';
  return null;
}

/**
 * The same hunk, seen from the other side.
 *
 * `hunkEdit` reads `original*` as "where the lines come from" and `modified*`
 * as "what range they replace". So the reverse merge is not a second algorithm
 * — it is the same one with the two sides swapped. That matters beyond
 * tidiness: a hand-written mirror image would be a second place for the gap
 * off-by-one to be got wrong, and it would be the less-exercised of the two.
 */
export function invertChange(change) {
  return {
    originalStartLineNumber: change.modifiedStartLineNumber,
    originalEndLineNumber: change.modifiedEndLineNumber,
    modifiedStartLineNumber: change.originalStartLineNumber,
    modifiedEndLineNumber: change.originalEndLineNumber,
  };
}

/**
 * Push one hunk from the modified (right) side into the original (left) text.
 * Returns the new left-hand text.
 */
export function applyHunkReverse(originalText, modifiedText, change) {
  return applyHunkToText(originalText, modifiedText, invertChange(change));
}
